import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Archive, Play, Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kuala_Lumpur" }) : "—");

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-lg font-bold font-mono" data-testid={testId}>{value}</p>
    </div>
  );
}

export function ArchiveSettings() {
  const [enabled, setEnabled] = useState(false);
  const [hotDays, setHotDays] = useState(30);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/archive/settings");
      setEnabled(!!res.data.enabled);
      setHotDays(res.data.hot_days || 30);
      setLastRun(res.data.last_run || null);
      setRunning(!!res.data.state?.running);
      setProgress(res.data.state || null);
    } catch (e) { /* settings tab shows defaults */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [running, load]);

  const save = async (nextEnabled, nextDays) => {
    setBusy("save");
    try {
      const res = await api.put("/api/archive/settings", { enabled: nextEnabled, hot_days: Number(nextDays) });
      setEnabled(res.data.enabled); setHotDays(res.data.hot_days);
      toast.success(res.data.enabled ? "Nightly archiving enabled (02:30 MYT)" : "Nightly archiving disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setBusy(""); }
  };

  const runDryRun = async () => {
    setBusy("dry");
    try { const res = await api.get("/api/archive/dry-run"); setDryRun(res.data); }
    catch (e) { toast.error("Dry run failed"); }
    finally { setBusy(""); }
  };

  const runNow = async () => {
    if (!window.confirm(`Move all flights older than ${hotDays} days to the archive now? Rows are verified in the archive before being removed from the hot collection.`)) return;
    setBusy("run");
    try {
      const res = await api.post("/api/archive/run");
      if (res.data.status === "started") { toast.success("Archive run started"); setRunning(true); }
      else toast.info("An archive run is already in progress");
    } catch (e) { toast.error("Failed to start archive run"); }
    finally { setBusy(""); }
  };

  return (
    <Card className="bg-card/50 border-border" data-testid="archive-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Archive className="h-5 w-5" />Data Archiving (hot / cold split)</CardTitle>
        <CardDescription>
          Keeps the live collections small and fast. Flights and integration logs older than the hot window move nightly to
          <code className="font-mono mx-1">flights_archive</code> / <code className="font-mono mx-1">integration_logs_archive</code> with all flight information and the raw fields the app uses; the verbatim XML echo is trimmed. Nothing is deleted before it is verified in the archive.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-end gap-6">
          <div className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={(v) => save(v, hotDays)} disabled={busy === "save"} data-testid="archive-enabled-switch" />
            <div>
              <Label className="text-sm">Nightly archive job</Label>
              <p className="text-[11px] text-muted-foreground">Runs 02:30 Malaysia time{enabled ? "" : " — currently off"}</p>
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Hot window (days)</Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={7} value={hotDays} onChange={(e) => setHotDays(e.target.value)} className="w-24 h-8 bg-background" data-testid="archive-hot-days-input" />
              <Button size="sm" variant="outline" className="h-8" onClick={() => save(enabled, hotDays)} disabled={busy === "save"} data-testid="archive-save-days-btn">Save</Button>
            </div>
          </div>
          <Badge variant="outline" className={enabled ? "bg-green-500/10 text-green-500 border-green-500/20" : "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"} data-testid="archive-status-badge">
            {running ? "RUNNING" : enabled ? "ENABLED" : "DISABLED"}
          </Badge>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={runDryRun} disabled={!!busy} data-testid="archive-dry-run-btn">
            {busy === "dry" ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}Dry run — show what would move
          </Button>
          <Button size="sm" onClick={runNow} disabled={!!busy || running} data-testid="archive-run-now-btn">
            <Play className="h-4 w-4 mr-2" />Run archive now
          </Button>
        </div>

        {running && progress && (
          <p className="text-xs text-amber-500 animate-pulse" data-testid="archive-progress">
            Archiving… {progress.moved} / {progress.total} flights, {progress.moved_logs ?? 0} / {progress.total_logs ?? 0} logs (started {fmt(progress.started_at)})
          </p>
        )}

        {dryRun && (
          <div className="space-y-2" data-testid="archive-dry-run-result">
            <p className="text-xs text-muted-foreground">Cutoff: records received before <span className="font-mono">{fmt(dryRun.cutoff)}</span></p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Would move" value={dryRun.would_move.toLocaleString()} testId="dry-run-would-move" />
              <Stat label="Stay hot" value={dryRun.hot_remaining.toLocaleString()} testId="dry-run-hot-remaining" />
              <Stat label="Already archived" value={dryRun.archive_total.toLocaleString()} testId="dry-run-archive-total" />
              <Stat label="Est. space freed" value={`${dryRun.est_freed_mb} MB`} testId="dry-run-freed" />
              <Stat label="Logs would move" value={(dryRun.logs_would_move ?? 0).toLocaleString()} testId="dry-run-logs-would-move" />
              <Stat label="Logs stay hot" value={((dryRun.logs_hot_total ?? 0) - (dryRun.logs_would_move ?? 0)).toLocaleString()} testId="dry-run-logs-hot" />
              <Stat label="Logs archived" value={(dryRun.logs_archive_total ?? 0).toLocaleString()} testId="dry-run-logs-archive-total" />
            </div>
            <p className="text-[11px] text-muted-foreground">Hot collection: {dryRun.hot_size_mb} MB · oldest hot record {fmt(dryRun.oldest_hot_record)}</p>
          </div>
        )}

        <div className="text-xs text-muted-foreground border-t border-border pt-3" data-testid="archive-last-run">
          <span className="font-medium text-foreground">Last run:</span>{" "}
          {lastRun ? (
            <>
              {fmt(lastRun.started_at)} — {lastRun.status === "ok" ? <span className="text-green-500">OK</span> : <span className="text-red-500">ERROR: {lastRun.error}</span>}, moved {lastRun.moved?.toLocaleString()} flights and {(lastRun.moved_logs ?? 0).toLocaleString()} logs
            </>
          ) : "never"}
        </div>
      </CardContent>
    </Card>
  );
}
